/**
 * Symmetric-margin proposal card (the runtime HITL gate). Pure presentational:
 * it shows the RESOLVED target (name + number + current volume), the margin in
 * CENTIMETRES with its direction, the previewed volume change, and the new
 * structure name; nothing mutates until the clinician presses Apply. The card
 * frame, status chip, hover preview, and Apply/Reject gate live in
 * {@link ProposalCardShell}; this component supplies only the body.
 */
import {
    formatCmTrimmed,
    formatCmWithUnit,
    formatSignedCm3,
} from '~/utils/units';
import GridRoundingNote from './GridRoundingNote';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface MarginProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    /** Signed cm: positive = expand (outer), negative = contract (inner). */
    readonly marginCm: number;
    /**
     * The clinician's ORIGINAL request, set only when it was finer than the
     * 0.1 mm grid and rounded to {@link marginCm}. Drives the disclosure note.
     */
    readonly requestedMarginCm?: number;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    /** Hover/focus the card to preview the proposed geometry in the viewport. */
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function MarginProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    marginCm,
    requestedMarginCm,
    newRoi,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: MarginProposalCardProps) {
    const direction = marginCm >= 0 ? 'expand' : 'contract';

    return (
        <ProposalCardShell
            title="Symmetric margin"
            ariaLabel="Symmetric margin proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                {formatCmWithUnit(Math.abs(marginCm))} {direction} on “
                {targetName}”{' → '}
                <strong>{newRoi}</strong>
            </ProposalSummaryBox>

            {requestedMarginCm !== undefined && (
                <GridRoundingNote>
                    Rounded from {formatCmTrimmed(Math.abs(requestedMarginCm))}{' '}
                    cm to the 0.1 mm grid.
                </GridRoundingNote>
            )}

            <PreviewedVolumeLine
                label="Previewed volume change"
                value={formatSignedCm3(volumeDeltaCm3)}
            />
        </ProposalCardShell>
    );
}
